// combatpro/runtime/dmOpsEngine.js
//
// Combat PRO - encounter-level DM Operations engine (pure).
//
// Owns the encounter-scoped structures and computations for the DM Operations
// Expansion that are NOT per-combatant runtime (that lives in combatRuntime.js):
// Combat Groups, Battlefield Zones, Triggered-Effect matching (bounded, no
// scripting), Live Threat estimation, Encounter Template runtime-stripping, mass /
// group target resolution, and the After-Action report. Pure: data in, data out,
// no React, no storage, no side effects. No optional chaining, no nullish
// coalescing, no em dash.

import { getRt, withRt, isOutOfFight, setEntered, SIDES, SIDE_LABELS, OUTCOME_LABELS, OUT_OF_FIGHT } from './combatRuntime';

export function newDmId(prefix) {
  var p = prefix ? prefix : 'dm';
  return p + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7);
}
function isObj(x) { return x && typeof x === 'object'; }
function str(x, d) { return (typeof x === 'string') ? x : d; }

// ---- Combat Groups (encounter-defined; e.g. Entrance Guards, Ritualists, Boss) --
export function newGroup(name, side) {
  return { id: newDmId('grp'), name: str(name, ''), side: (SIDES.indexOf(side) !== -1) ? side : 'enemy', note: '' };
}
export function normalizeGroups(arr) {
  if (!Array.isArray(arr)) { return []; }
  return arr.filter(isObj).map(function (g) {
    return { id: str(g.id, newDmId('grp')), name: str(g.name, ''), side: (SIDES.indexOf(g.side) !== -1) ? g.side : 'enemy', note: str(g.note, '') };
  });
}
export function defaultDmOps() { return { groups: [], triggeredEffects: [], quickActions: [] }; }
export function normalizeDmOps(d) {
  var o = defaultDmOps();
  if (!isObj(d)) { return o; }
  o.groups = normalizeGroups(d.groups);
  o.triggeredEffects = normalizeTriggeredEffects(d.triggeredEffects);
  o.quickActions = normalizeQuickActions(d.quickActions);
  return o;
}

// ---- Battlefield Zones (owned by Encounter Brief -> Battle Setup) -----------
export function newZone(name) {
  return { id: newDmId('zone'), name: str(name, ''), description: '', tags: '', hazard: '', entrance: false, note: '' };
}
export function normalizeZones(arr) {
  if (!Array.isArray(arr)) { return []; }
  return arr.filter(isObj).map(function (z) {
    return {
      id: str(z.id, newDmId('zone')), name: str(z.name, ''), description: str(z.description, ''),
      tags: str(z.tags, ''), hazard: str(z.hazard, ''), entrance: z.entrance === true, note: str(z.note, '')
    };
  });
}

// ---- Triggered Effects (bounded model + matcher; no code execution) ---------
export var TRIGGER_WHENS = [
  'turn-start', 'turn-end', 'round-start', 'round-end', 'on-hit', 'on-damaged', 'on-healed',
  'save-success', 'save-fail', 'condition-applied', 'condition-removed', 'hp-threshold',
  'combatant-defeated', 'zone-entered', 'zone-left', 'event-activated', 'manual'
];
export var TRIGGER_WHEN_LABELS = {
  'turn-start': 'Start of turn', 'turn-end': 'End of turn', 'round-start': 'Start of round',
  'round-end': 'End of round', 'on-hit': 'On hit', 'on-damaged': 'On damage taken', 'on-healed': 'On healing',
  'save-success': 'Successful save', 'save-fail': 'Failed save', 'condition-applied': 'Condition applied',
  'condition-removed': 'Condition removed', 'hp-threshold': 'HP threshold', 'combatant-defeated': 'Combatant defeated',
  'zone-entered': 'Zone entered', 'zone-left': 'Zone left', 'event-activated': 'Encounter Event activated', 'manual': 'Manual'
};
export var RESULT_KINDS = [
  'damage', 'healing', 'apply-condition', 'remove-condition', 'apply-effect', 'remove-effect',
  'spend-resource', 'restore-resource', 'activate-event', 'tactical-change', 'dm-prompt'
];
export var RESULT_KIND_LABELS = {
  damage: 'Damage', healing: 'Healing', 'apply-condition': 'Apply condition', 'remove-condition': 'Remove condition',
  'apply-effect': 'Apply effect', 'remove-effect': 'Remove effect', 'spend-resource': 'Spend resource',
  'restore-resource': 'Restore resource', 'activate-event': 'Activate event', 'tactical-change': 'Tactical change', 'dm-prompt': 'DM prompt / reminder'
};
export var QUICK_KINDS = ['dice', 'weapon', 'ability', 'spell', 'damage', 'healing', 'condition', 'effect', 'resource', 'event', 'hazard', 'tactical', 'note'];
export var QUICK_KIND_LABELS = {
  dice: 'Dice', weapon: 'Weapon', ability: 'Ability', spell: 'Spell', damage: 'Damage', healing: 'Healing',
  condition: 'Condition', effect: 'Effect', resource: 'Resource', event: 'Event', hazard: 'Hazard', tactical: 'Tactical', note: 'Log note'
};
export function newQuickAction(kind) {
  return { id: newDmId('qa'), label: '', kind: (QUICK_KINDS.indexOf(kind) !== -1) ? kind : 'note', detail: '' };
}
export function normalizeQuickActions(arr) {
  if (!Array.isArray(arr)) { return []; }
  return arr.filter(isObj).map(function (q) {
    return { id: str(q.id, newDmId('qa')), label: str(q.label, ''), kind: (QUICK_KINDS.indexOf(q.kind) !== -1) ? q.kind : 'note', detail: str(q.detail, '') };
  });
}

export function newTriggeredEffect(when) {
  return {
    id: newDmId('trg'), name: '', when: (TRIGGER_WHENS.indexOf(when) !== -1) ? when : 'manual',
    round: null, hpPct: null, resultKind: 'dm-prompt', detail: '', enabled: true, lastFiredKey: ''
  };
}
export function normalizeTriggeredEffects(arr) {
  if (!Array.isArray(arr)) { return []; }
  return arr.filter(isObj).map(function (t) {
    var n = Number(t.round); var h = Number(t.hpPct);
    return {
      id: str(t.id, newDmId('trg')), name: str(t.name, ''),
      when: (TRIGGER_WHENS.indexOf(t.when) !== -1) ? t.when : 'manual',
      round: isNaN(n) ? null : Math.trunc(n), hpPct: isNaN(h) ? null : Math.trunc(h),
      resultKind: (RESULT_KINDS.indexOf(t.resultKind) !== -1) ? t.resultKind : 'dm-prompt',
      detail: str(t.detail, ''), enabled: t.enabled !== false, lastFiredKey: str(t.lastFiredKey, '')
    };
  });
}
// Which enabled triggers match a live event? Pure; returns the matching triggers.
// ctx = { when, round, hpPct }. HP/round guards are checked when present. Duplicate
// suppression is the caller's job via lastFiredKey (compare against ctx.fireKey).
export function matchTriggers(triggers, ctx) {
  if (!Array.isArray(triggers) || !isObj(ctx)) { return []; }
  return triggers.filter(function (t) {
    if (!t || t.enabled === false) { return false; }
    if (t.when !== ctx.when) { return false; }
    if (t.when === 'hp-threshold' && typeof t.hpPct === 'number' && typeof ctx.hpPct === 'number') {
      return ctx.hpPct <= t.hpPct;
    }
    if ((t.when === 'round-start' || t.when === 'round-end') && typeof t.round === 'number' && typeof ctx.round === 'number') {
      return ctx.round >= t.round;
    }
    return true;
  });
}

// ---- Live Threat / encounter difficulty (deterministic; no false precision) -
export function crToNumber(cr) {
  if (typeof cr === 'number') { return cr; }
  var s = String(cr == null ? '' : cr).trim();
  if (!s) { return 0; }
  if (s.indexOf('/') !== -1) {
    var parts = s.split('/');
    var a = Number(parts[0]); var b = Number(parts[1]);
    if (!isNaN(a) && !isNaN(b) && b !== 0) { return a / b; }
    return 0;
  }
  var n = Number(s);
  return isNaN(n) ? 0 : n;
}
export function populatedMonsters(monsters) {
  if (!Array.isArray(monsters)) { return []; }
  return monsters.filter(function (m) {
    return m && ((m.customName && String(m.customName).trim()) || (m.selectedMonster && String(m.selectedMonster).trim()));
  });
}
// input: { players:[], monsters:[], pendingWaves:Number }. Deterministic summary.
export function computeThreat(input) {
  var players = (input && Array.isArray(input.players)) ? input.players : [];
  var monsters = populatedMonsters(input ? input.monsters : []);
  var partySize = players.length;
  var enemies = monsters.filter(function (m) {
    var rt = getRt(m);
    if (rt.entered === false) { return false; }
    var side = rt.side;
    if (side && side !== 'enemy') { return false; }
    return !isOutOfFight(rt);
  });
  var defeated = monsters.filter(function (m) { return isOutOfFight(getRt(m)); }).length;
  var crSum = 0;
  enemies.forEach(function (m) { crSum += crToNumber(m.cr); });
  var pendingWaves = (input && typeof input.pendingWaves === 'number') ? input.pendingWaves : 0;
  // Heuristic ratio of summed enemy CR to (party size). Deterministic bands.
  var ratio = partySize > 0 ? (crSum / partySize) : crSum;
  var band = 'Trivial';
  if (ratio >= 4) { band = 'Deadly'; }
  else if (ratio >= 2.5) { band = 'Hard'; }
  else if (ratio >= 1.2) { band = 'Moderate'; }
  else if (ratio > 0) { band = 'Easy'; }
  return {
    band: band, partySize: partySize, activeEnemies: enemies.length, defeatedEnemies: defeated,
    enemyCrSum: Math.round(crSum * 100) / 100, pendingWaves: pendingWaves
  };
}

// ---- Mass / group target resolution ----------------------------------------
// Resolve which populated monster indices match a selector against the live array.
// selector = { side, group, ids:[indices], scope:'all-enemies'|'all'|'selected'|'side'|'group' }.
export function resolveMassTargets(monsters, selector) {
  var arr = Array.isArray(monsters) ? monsters : [];
  var out = [];
  var sel = isObj(selector) ? selector : {};
  for (var i = 0; i < arr.length; i++) {
    var m = arr[i];
    if (!m) { continue; }
    var populated = (m.customName && String(m.customName).trim()) || (m.selectedMonster && String(m.selectedMonster).trim());
    if (!populated) { continue; }
    var rt = getRt(m);
    var match = false;
    if (sel.scope === 'all') { match = true; }
    else if (sel.scope === 'all-enemies') { match = (!rt.side || rt.side === 'enemy'); }
    else if (sel.scope === 'side') { match = (rt.side === sel.side); }
    else if (sel.scope === 'group') { match = (rt.group === sel.group); }
    else if (sel.scope === 'selected') { match = Array.isArray(sel.ids) && sel.ids.indexOf(i) !== -1; }
    if (match && !isOutOfFight(rt)) { out.push(i); }
  }
  return out;
}

// ---- Encounter Template runtime-strip --------------------------------------
// Given a live encounter slot, return a template that keeps PREPARATION and drops
// live runtime. Never mutates the input.
export function stripRuntimeForTemplate(encounter) {
  if (!isObj(encounter)) { return null; }
  var monsters = Array.isArray((encounter.combat || {}).monsters) ? encounter.combat.monsters : [];
  var cleanMonsters = monsters.map(function (m) {
    if (!m) { return m; }
    var copy = Object.assign({}, m);
    // drop live runtime: current HP resets to max, spent legendary actions, rt runtime.
    copy.currentHP = (typeof m.maxHP === 'number') ? m.maxHP : m.currentHP;
    copy.usedLegendaryActions = 0;
    copy.conditions = [];
    if (copy.rt) {
      copy.rt = {
        // keep prep-relevant rt (side/group), reset live rt
        side: m.rt.side ? m.rt.side : null, group: m.rt.group ? m.rt.group : null,
        legendaryResistance: m.rt.legendaryResistance ? { max: m.rt.legendaryResistance.max, used: 0 } : { max: 0, used: 0 },
        resources: Array.isArray(m.rt.resources) ? m.rt.resources.map(function (r) { return Object.assign({}, r, { current: r.max }); }) : []
      };
    }
    return copy;
  });
  return {
    id: newDmId('tpl'),
    name: ((encounter.meta && encounter.meta.name) ? encounter.meta.name : 'Encounter') + ' Template',
    createdAt: Date.now(),
    meta: Object.assign({}, encounter.meta, { status: 'Design', lastModified: Date.now() }),
    encounterBrief: encounter.encounterBrief ? encounter.encounterBrief : null,
    dmOps: encounter.dmOps ? encounter.dmOps : null,
    monsters: cleanMonsters
  };
}

// ---- After-Action report ---------------------------------------------------
export function buildAfterAction(input) {
  var enc = (input && input.encounter) ? input.encounter : {};
  var players = (input && Array.isArray(input.players)) ? input.players : [];
  var monsters = populatedMonsters(input ? input.monsters : []);
  var log = (input && Array.isArray(input.log)) ? input.log : [];
  var round = (input && typeof input.round === 'number') ? input.round : 1;
  var brief = enc.encounterBrief ? enc.encounterBrief : null;
  var gen = enc.gen ? enc.gen : null;

  var byOutcome = {};
  OUT_OF_FIGHT.forEach(function (o) { byOutcome[o] = []; });
  monsters.forEach(function (m) {
    var rt = getRt(m);
    if (isOutOfFight(rt)) {
      var nm = (m.customName && String(m.customName).trim()) ? m.customName.trim() : (m.selectedMonster || 'Monster');
      byOutcome[rt.outcome].push(nm);
    }
  });

  var events = (brief && Array.isArray(brief.events)) ? brief.events : [];
  var resolvedEvents = events.filter(function (e) { return e.status === 'resolved'; }).map(function (e) { return e.name || 'Event'; });
  var rules = (brief && Array.isArray(brief.rules)) ? brief.rules : [];
  var hazards = rules.filter(function (r) { return r.kind === 'hazard'; }).map(function (r) { return r.title || 'Hazard'; });

  var participantPlayers = players.map(function (p) { return p && p.name ? p.name : null; }).filter(Boolean);
  var participantMonsters = monsters.map(function (m) { return (m.customName && String(m.customName).trim()) ? m.customName.trim() : (m.selectedMonster || null); }).filter(Boolean);
  var companionParticipation = players.filter(function (p) { return p && p.companion; }).map(function (p) { return p.name || 'Companion'; });
  var escapedOrSurrendered = (byOutcome.fled || []).concat(byOutcome.surrendered || []);
  var objectiveFact = (brief && brief.goal && brief.goal.objective) ? [{ objective: brief.goal.objective, resolution: brief.goal.resolution || 'open' }] : [];
  var combatantState = players.filter(Boolean).map(function (p) {
    var value = { name: p.name || 'Player' };
    if (typeof p.currentHP === 'number') { value.currentHP = p.currentHP; }
    if (typeof p.maxHP === 'number') { value.maxHP = p.maxHP; }
    if (Array.isArray(p.conditions) && p.conditions.length) { value.conditions = p.conditions.slice(); }
    return value;
  });
  var notableEvents = log.map(function (entry) {
    if (typeof entry === 'string') { return entry; }
    if (entry && typeof entry.message === 'string') { return entry.message; }
    if (entry && typeof entry.text === 'string') { return entry.text; }
    return null;
  }).filter(Boolean);
  var factions = monsters.map(function (m) { return m.faction || m.organization || (m.rt && m.rt.faction) || null; }).filter(Boolean);
  var locationState = {};
  if (enc.meta && enc.meta.location) { locationState.location = enc.meta.location; }
  if (brief && brief.location) { locationState.location = brief.location; }
  if (brief && brief.environment) { locationState.environment = brief.environment; }
  if (hazards.length) { locationState.hazards = hazards; }
  var facts = {
    participants: { players: participantPlayers, opponents: participantMonsters },
    outcome: {
      status: monsters.length && monsters.every(function (m) { return isOutOfFight(getRt(m)); }) ? 'RESOLVED' : 'IN_PROGRESS',
      objective_resolution: (brief && brief.goal) ? (brief.goal.resolution || 'open') : null,
      opponent_outcomes: byOutcome
    },
    combat_state: { rounds_elapsed: round, player_state: combatantState }
  };
  if (objectiveFact.length) { facts.objectives = objectiveFact; }
  if (escapedOrSurrendered.length) { facts.escaped_or_surrendered_opponents = escapedOrSurrendered; }
  if (enc.treasure) { facts.items_treasure = enc.treasure; }
  if (companionParticipation.length) { facts.companion_participation = companionParticipation; }
  if (notableEvents.length) { facts.notable_events = notableEvents; }
  if (factions.length) { facts.faction_involvement = Array.from(new Set(factions)); }
  if (Object.keys(locationState).length) { facts.location_battlefield_state = locationState; }
  if (resolvedEvents.length) { facts.triggered_consequences = resolvedEvents; }

  return {
    encounterName: (enc.meta && enc.meta.name) ? enc.meta.name : 'Encounter',
    family: (input && input.family) ? input.family : 'standard',
    localSlot: (input && typeof input.localSlot === 'number') ? input.localSlot : null,
    waveCount: (gen && gen.waveCount) ? gen.waveCount : 0,
    waveStyle: (gen && gen.style) ? gen.style : '',
    waveSummary: (gen && Array.isArray(gen.waveSummary)) ? gen.waveSummary : [],
    roundsElapsed: round,
    objective: (brief && brief.goal) ? brief.goal.objective : '',
    objectiveResolution: (brief && brief.goal) ? brief.goal.resolution : 'open',
    partyCount: players.length,
    enemyCount: monsters.length,
    outcomes: byOutcome,
    resolvedEvents: resolvedEvents,
    hazards: hazards,
    logCount: log.length,
    facts: facts,
    generatedAt: Date.now()
  };
}

export { SIDES, SIDE_LABELS, OUTCOME_LABELS, OUT_OF_FIGHT };


// ---- Automatic sequential Wave progression (spec correction #3) --------------
// A generated Wave Combat Group is RESOLVED when all its ENTERED members are
// out-of-fight by OUTCOME STATE (defeated/dead/fled/surrendered/captured/removed,
// via isOutOfFight) - not merely HP=0. When a wave resolves, the next wave (its
// members flagged rt.entered=false) automatically enters. Pure + deterministic so
// automatic Wave 1 -> 2 -> 3 progression is provable without a live combat.
export function isGroupResolved(monsters, groupId) {
  var arr = populatedMonsters(monsters);
  var members = arr.filter(function (m) { var rt = getRt(m); return rt.group === groupId && rt.entered !== false; });
  if (!members.length) { return false; }
  return members.every(function (m) { return isOutOfFight(getRt(m)); });
}
export function applyWaveProgression(monsters, groups) {
  var arr = Array.isArray(monsters) ? monsters : [];
  var order = Array.isArray(groups) ? groups : [];
  if (order.length < 2) { return { monsters: arr, enteredWaves: [], changed: false }; }
  for (var w = 0; w < order.length - 1; w++) {
    var curId = order[w].id;
    var nextId = order[w + 1].id;
    if (!isGroupResolved(arr, curId)) { continue; }
    var pending = arr.filter(function (m) { var rt = getRt(m); return rt.group === nextId && rt.entered === false; });
    if (!pending.length) { continue; }
    var next = arr.map(function (m) {
      var rt = getRt(m);
      if (rt.group === nextId && rt.entered === false) { return withRt(m, function (r) { return setEntered(r, true); }); }
      return m;
    });
    return { monsters: next, enteredWaves: [w + 2], changed: true };
  }
  return { monsters: arr, enteredWaves: [], changed: false };
}
